import { NodeViewContextableWrapper } from "@ext/markdown/core/element/NodeViewContextableWrapper";
import { BlockCaption } from "@ext/markdown/elements/article/edit/helpers/BlockCaption";
import { GROUP_ANCHOR_ATTR } from "@ext/markdown/elements/article/edit/helpers/getToolbarReferenceRect";
import { useBlockToolbar } from "@ext/markdown/elements/article/edit/helpers/useBlockToolbar";
import GalleryActions from "@ext/markdown/elements/gallery/edit/components/GalleryActions";
import { type GalleryItem, toGalleryItem } from "@ext/markdown/elements/gallery/logic/getGalleryItemRatio";
import { toGalleryView } from "@ext/markdown/elements/gallery/model/gallery";
import GalleryFrame from "@ext/markdown/elements/gallery/render/components/GalleryFrame";
import GalleryTitle from "@ext/markdown/elements/gallery/render/components/GalleryTitle";
import getGalleryItemsClassName from "@ext/markdown/elements/gallery/render/components/galleryItemsClassName";
import { NodeViewContent, type NodeViewProps } from "@tiptap/react";
import { type MouseEvent, type ReactElement, useRef, useState } from "react";

const selectGalleryOnFrameClick =
	({ editor, getPos }: NodeViewProps) =>
	(event: MouseEvent<HTMLElement>) => {
		if (!editor || editor.isDestroyed || !editor.isEditable) return;
		const target = event.target as HTMLElement;
		if (target.closest("[data-gallery-items], input, button, .resizer")) return;
		event.preventDefault();
		editor.commands.setNodeSelection(getPos());
	};

const EditGallery = (props: NodeViewProps): ReactElement => {
	const { editor, node, getPos, updateAttributes, selected } = props;
	const isEditable = editor.isEditable;
	const signatureRef = useRef<HTMLInputElement>(null);
	const [hasSignature, setHasSignature] = useState(isEditable && node.attrs.title?.length > 0);
	const onFrameMouseDown = selectGalleryOnFrameClick(props);

	const items: GalleryItem[] = [];
	node.forEach((child) => items.push(toGalleryItem(child.attrs)));

	useBlockToolbar(
		props,
		isEditable && (
			<GalleryActions
				editor={editor}
				getPos={getPos}
				hasSignature={hasSignature}
				node={node}
				setHasSignature={setHasSignature}
				signatureRef={signatureRef}
				updateAttributes={updateAttributes}
			/>
		),
	);

	const caption = isEditable ? (
		<BlockCaption
			editor={editor}
			getPos={getPos}
			setVisible={setHasSignature}
			signatureRef={signatureRef}
			text={node.attrs.title}
			updateAttributes={updateAttributes}
			visible={hasSignature}
		/>
	) : (
		node.attrs.title && <GalleryTitle>{node.attrs.title}</GalleryTitle>
	);

	return (
		<NodeViewContextableWrapper onMouseDown={onFrameMouseDown} props={props}>
			<GalleryFrame
				caption={caption}
				items={items}
				onScaleChange={isEditable ? (scale) => updateAttributes({ scale }) : undefined}
				scale={node.attrs.scale}
				selected={selected}
				view={node.attrs.view}
			>
				<NodeViewContent
					className={getGalleryItemsClassName(toGalleryView(node.attrs.view))}
					data-gallery-items=""
					{...{ [GROUP_ANCHOR_ATTR]: "" }}
				/>
			</GalleryFrame>
		</NodeViewContextableWrapper>
	);
};

export default EditGallery;
